'use client'

import React, { useState, useMemo } from 'react'
import { BookOpen, ArrowRight, Search, X } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import type { Guide, Media } from '@/payload-types'
import { getMediaUrl } from '../../lib/media'

type SortOption = 'alpha-asc' | 'alpha-desc'

interface GuidesListClientProps {
  guides: Guide[]
}

// Strips accents and punctuation (hyphens included) within a single word so visa-type
const DIACRITIC_MARKS_REGEX = new RegExp('[\\u0300-\\u036f]', 'g')

function normalizeWord(word: string): string {
  return word
    .toLowerCase()
    .normalize('NFD')
    .replace(DIACRITIC_MARKS_REGEX, '')
    .replace(/[^a-z0-9]/g, '')
}

// Word-level tokens rather than one flattened string, so a query like "b1" can't match
// mid-word inside an unrelated code such as "EB-1A" the way a raw substring search would.
function guideSearchWords(guide: Guide): string[] {
  return [guide.name, guide.title, guide.subtitle, guide.visaType, guide.description, guide.shortDescription]
    .filter(Boolean)
    .join(' ')
    .split(/\s+/)
    .map(normalizeWord)
    .filter(Boolean)
}

export function GuidesListClient({ guides }: GuidesListClientProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedVisaType, setSelectedVisaType] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<SortOption>('alpha-asc')

  // Extract unique visa types for filter dropdown
  const visaTypes = useMemo(() => {
    const types = guides.map((g) => g.visaType).filter((v): v is string => !!v)
    return [...new Set(types)].sort()
  }, [guides])

  // Filter and sort guides
  const filteredGuides = useMemo(() => {
    let result = guides

    const queryWords = searchQuery.trim().split(/\s+/).map(normalizeWord).filter(Boolean)
    if (queryWords.length > 0) {
      result = result.filter((g) => {
        const guideWords = guideSearchWords(g)
        return queryWords.every((qw) => guideWords.some((w) => w.startsWith(qw)))
      })
    }

    // Visa type filter
    if (selectedVisaType) {
      result = result.filter((g) => g.visaType === selectedVisaType)
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'alpha-asc':
          return (a.name || '').localeCompare(b.name || '')
        case 'alpha-desc':
          return (b.name || '').localeCompare(a.name || '')
        default:
          return 0
      }
    })

    return result
  }, [guides, searchQuery, selectedVisaType, sortBy])

  return (
    <div>
      {/* Search + Filters Bar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search guides..."
            className="w-full pl-12 pr-10 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Visa Type Dropdown */}
        {visaTypes.length > 0 && (
          <select
            value={selectedVisaType || ''}
            onChange={(e) => setSelectedVisaType(e.target.value || null)}
            className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold transition-all cursor-pointer sm:w-auto"
          >
            <option value="">All Visa Types</option>
            {visaTypes.map((vt) => (
              <option key={vt} value={vt}>
                {vt}
              </option>
            ))}
          </select>
        )}

        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortOption)}
          className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold transition-all cursor-pointer sm:w-auto"
        >
          <option value="alpha-asc">A → Z</option>
          <option value="alpha-desc">Z → A</option>
        </select>
      </div>

      {/* Results count */}
      {(searchQuery || selectedVisaType) && (
        <p className="text-sm text-gray-600 mb-6">
          {filteredGuides.length} {filteredGuides.length === 1 ? 'guide' : 'guides'} found
          {selectedVisaType && (
            <span>
              {' '}
              for <strong>{selectedVisaType}</strong>
            </span>
          )}
          {searchQuery && <span> matching &ldquo;{searchQuery}&rdquo;</span>}
        </p>
      )}

      {/* Guides Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredGuides.map((guide) => {
          const heroImage = guide.heroImage as Media | null | undefined
          const chapters = guide.chapters || []
          const visaType = guide.visaType

          return (
            <Link
              key={guide.id}
              href={`/guides/${guide.slug}`}
              className="group flex flex-col bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              {/* Image */}
              <div className="relative aspect-4/3 overflow-hidden bg-brand-blue shrink-0">
                {getMediaUrl(heroImage, 'card') ? (
                  <Image
                    src={getMediaUrl(heroImage, 'card')}
                    alt={guide.name || 'Guide cover'}
                    fill
                    className="object-cover opacity-60 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookOpen className="w-16 h-16 text-white/20" strokeWidth={1} />
                  </div>
                )}

                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-linear-to-t from-brand-blue/80 via-brand-blue/30 to-transparent" />

                {/* Visa Type Badge */}
                {visaType && (
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center px-2.5 py-1 bg-brand-gold/90 rounded-full text-xs font-semibold text-white tracking-wide uppercase">
                      {visaType}
                    </span>
                  </div>
                )}

                {/* Chapter Count Badge */}
                <div className="absolute bottom-3 left-3">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                    <BookOpen className="w-3 h-3 text-brand-gold-light" />
                    <span className="text-xs font-medium text-white/90">
                      {chapters.length} {chapters.length === 1 ? 'Chapter' : 'Chapters'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 p-5 md:p-6 flex flex-col">
                {/* Guide Title */}
                <h2 className="text-lg md:text-xl font-serif text-primary mb-1.5 group-hover:text-brand-gold transition-colors leading-snug">
                  {(guide.title || guide.name).split('\\n').map((line, i, arr) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </h2>

                {/* Subtitle */}
                {guide.subtitle && (
                  <p className="text-sm text-brand-gold font-medium mb-2">{guide.subtitle}</p>
                )}

                {/* Description */}
                {(guide.description || guide.shortDescription) && (
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2 flex-1">
                    {guide.description || guide.shortDescription}
                  </p>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-auto">
                  {guide.year && (
                    <span className="text-xs text-gray-500 font-medium">Updated {guide.year}</span>
                  )}
                  <div className="flex items-center gap-1.5 text-brand-gold text-sm font-medium ml-auto">
                    <span>Read</span>
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>
          )
        })}
      </div>

      {/* Empty State */}
      {filteredGuides.length === 0 && (
        <div className="text-center py-20">
          <BookOpen className="w-16 h-16 text-gray-500 mx-auto mb-4" strokeWidth={1} />
          <h2 className="text-2xl font-serif text-gray-500 mb-2">No guides found</h2>
          <p className="text-gray-600 mb-4">
            {searchQuery || selectedVisaType
              ? 'Try adjusting your search or filters.'
              : 'Check back soon for comprehensive immigration guides.'}
          </p>
          {(searchQuery || selectedVisaType) && (
            <button
              onClick={() => {
                setSearchQuery('')
                setSelectedVisaType(null)
              }}
              className="text-brand-gold font-medium hover:text-brand-gold-light transition-colors"
            >
              Clear all filters
            </button>
          )}
        </div>
      )}
    </div>
  )
}
